import { getDataReaderClient } from "@multiweb/clients/util";
import { DataSourceErrorHandler } from "UI/UI/UI/DataSourceErrorHandler";
import { AdditionalOrdering, ConditionParam, IDataSource, IDataSourceLoadInfo, IDBDataSourceParams, IOrderBy, OrderType } from "../../../Global/Base/DataSources/Interfaces";
import { Event } from "../../../Global/Base/Events";
import { InvalidOperationException } from "../../../Global/Base/Exceptions";
import { CancelError, CancellationToken, CancellationTokenSource } from "../../../Global/Base/Promises/CancellationTokenSource";
import { ConditionOperator, StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { DBDataSource } from "./DBDataSource";

/*
* Trida reprezentujici databazovy zdroj dat pro konzumenta dat
*/
export class GroupingTreeViewDataSource extends DBDataSource {
    //#region Events
    /**
     * Vraci startindex pro cteni dat
     */
    public override dataLoaded: Event<IDataSource, IGroupingDataSourceLoadInfo> = new Event<IDataSource, IGroupingDataSourceLoadInfo>();
    //#endregion Events

    //#region Properties
    /** Pokud je nastaveno true, všechno třídění bude automaticky nezávislé na diakritice */
    private _orderByDefaultLinguistic = true;

    public override get orderBy(): IOrderBy[] {
        return this._orderBy;
    }

    public override set orderBy(values: IOrderBy[]) {
        this._orderBy = values;
        if (values && values.length > 0) {
            const resultOrderBy: string[] = [];

            for (const orderByItem of values) {
                resultOrderBy.push(this._parseOrderBy(orderByItem));
            }

            this._internalOrderBy = resultOrderBy;
        } else {
            this._internalOrderBy = [this.dataKey]; // při nezadaném sloupci pro třídění se defaultně použije sloupec ID
        }
    }

    //#endregion Properties

    //#region Constructors
    constructor(params: IDBDataSourceParams) {
        super(params);

        this.error.add((_sender, error) => {
            DataSourceErrorHandler.handleDataSourceError(error, this.hasFilterCondition);
        });
    }
    //#endregion Constructors

    protected override _parseOrderBy(orderBy: IOrderBy): string {
        if (this._orderByDefaultLinguistic)
            orderBy.additionalOrdering = AdditionalOrdering.linguistic;
        return super._parseOrderBy(orderBy);
    }

    /**
     * Získá data jedné konkrétní větve stromu
     * @param attribute Atribut, který má být zobrazen
     * @param additionalConditions Dodatečná data, potřebná ke čtení
     */
    public loadChildData(attribute: string, parentId: string, additionalConditions: Array<IAdditionalGroupingParams>, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        return this._loadTreeData(attribute, parentId, additionalConditions, cancellationToken);
    }

    /**
     * Nahraje data od zacatku
     */
    public override loadNewData(_keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        return this._loadTreeData(null, null, null, cancellationToken);
    }

    /**
     * Přečte data do požadované větve stromu
     * @param attribute Atribut, který má být zobrazen
     * @param additionalParams Dodatečná data, potřebná ke čtení
     */
    protected _loadTreeData(attribute: string, parentId: string, additionalParams: Array<IAdditionalGroupingParams>, cancellationToken: CancellationToken): Promise<any> {
        if (/@\d+/.test(this._condition) && (this._conditionParams == null || this._conditionParams.length === 0))
            return Promise.reject(new InvalidOperationException("Condition contains params but no parameter value available"));

        let finalCondition = this.condition;
        const finalConditionParams = [...this.conditionParams];

        if (additionalParams) {
            additionalParams.forEach((params) => {
                finalCondition = StringUtils.combineConditions(ConditionOperator.and, finalCondition, `${params.condition} = @${finalConditionParams.length}`); // `
                finalConditionParams.push(params.param);
            });
        }

        this._runningDataLoadCTS = new CancellationTokenSource([cancellationToken]);
        this._runningDataLoad = getDataReaderClient().getDataRowsWithParams(this._className, attribute ? [attribute] : this._attributes, finalCondition, {
            params: finalConditionParams,
            orderBy: (attribute ? [this._parseOrderBy({ attributeName: attribute, order: OrderType.ascending })] : this._internalOrderBy),
            dataLength: this._dataLength,
            readVersioned: this._readVersioned,
            distinct: true,
            groupBy: this._groupBy,
        }, this._fullLengthAttributes, this._getRequestContext(this._runningDataLoadCTS.token))
            .then(async (rows) => {
                this._internalRows = rows;

                const eventParams: IGroupingDataSourceLoadInfo = {
                    startIndex: 0,
                    dataGenerator: this._getIterator.bind(this, 0, rows.length),
                    lastBatch: true,
                    isFiltered: false,
                    refreshedData: false,
                    parentId: parentId,
                    attribute: attribute ? attribute : this._attributes[0],
                    rowsCount: this._internalRows.length,
                    emptyData: false,
                };

                this._runningDataLoad = null;

                this.dataLoaded.trigger(this, eventParams);
                this.loadingEnded.trigger(this, eventParams);

                return rows;
            }).catch((err) => {
                this._runningDataLoad = null;

                this.loadingEnded.trigger(this, { isFiltered: false, fulltextSearchString: this.fulltextCondition?.searchString, emptyData: true, cancellationToken: cancellationToken });

                // zrušení nahrávání data je v pořádku
                if (!(err instanceof CancelError))
                    this.error.trigger(this, err);
            });

        this.loadingStarted.trigger(this, 0);

        return this._runningDataLoad;
    }
}

/**
 * Dodatečná podmínka a vstup pro čtení dat
 */
export interface IAdditionalGroupingParams {
    condition: string;
    param: ConditionParam;
}

/**
 * Informace o přečtených datech
 * @generateInstanceClass
 */
export interface IGroupingDataSourceLoadInfo extends IDataSourceLoadInfo {
    /**
     * Identifikátor rodičovského prvku
     */
    parentId: string;
    /**
     * Atribut, ze kterého jsou načítána data
     */
    attribute: string;

    /**
     * Počet načtených řádků z DB
     */
    rowsCount: number;
}

export class IGroupingDataSourceLoadInfo {
    private static _properties = ["parentId", "attribute", "rowsCount"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IDataSourceLoadInfo;
    }
}
